<template>
  <div class="space-y-6">
    <div class="bg-white rounded-xl shadow-sm border border-neutral-200 p-6">
      <div class="flex items-center justify-between mb-4">
        <div>
          <h2 class="text-lg font-bold text-neutral-900">学科知识体系管理</h2>
          <p class="text-sm text-neutral-500 mt-1">
            管理学科及其层级：年段 → 年级 → 学期 → 单元（学科挂在「教学」场景分类下使用）
          </p>
        </div>
        <div class="flex gap-3">
          <button
            @click="openBatchImport"
            class="px-4 py-2 bg-secondary text-white rounded-lg hover:bg-secondary/90 transition-colors text-sm font-medium flex items-center gap-2"
          >
            <i class="fa fa-upload"></i>批量导入
          </button>
          <button
            @click="openSubjectAdd"
            class="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium flex items-center gap-2"
          >
            <i class="fa fa-plus"></i>新增学科
          </button>
        </div>
      </div>

      <div v-loading="loading" class="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <!-- 左：学科列表（两列并排） -->
        <div class="grid grid-cols-2 gap-2">
          <div
            v-for="sub in subjectList"
            :key="sub.id"
            class="flex items-center gap-2 rounded-xl border p-3 cursor-pointer transition-colors"
            :class="selectedSubject && selectedSubject.id === sub.id
              ? 'border-primary bg-primary/5'
              : 'border-neutral-200 bg-neutral-50 hover:border-neutral-300'"
            @click="selectSubject(sub)"
          >
            <div class="flex-1 min-w-0">
              <div class="font-bold text-neutral-900 text-sm truncate">{{ sub.title }}</div>
              <div class="text-xs" :class="sub.isEnable ? 'text-green-600' : 'text-neutral-400'">
                {{ sub.isEnable ? "启用" : "停用" }} · 排序 {{ sub.orderNum }}
              </div>
            </div>
            <button @click.stop="openSubjectEdit(sub)" class="text-neutral-400 hover:text-primary p-1" title="编辑">
              <i class="fa fa-pencil"></i>
            </button>
            <button @click.stop="deleteSubject(sub)" class="text-neutral-400 hover:text-red-500 p-1" title="删除">
              <i class="fa fa-trash-o"></i>
            </button>
          </div>
          <div v-if="!loading && subjectList.length === 0" class="text-center py-8 text-neutral-400 text-sm col-span-2">
            暂无学科
          </div>
        </div>

        <!-- 右：知识树 -->
        <div class="lg:col-span-3">
          <div v-if="!selectedSubject" class="text-center py-16 text-neutral-400 text-sm border border-dashed border-neutral-200 rounded-xl">
            点击左侧学科，维护其知识体系（年段/年级/学期/单元）
          </div>
          <div v-else class="space-y-3">
            <div class="flex items-center justify-between">
              <h3 class="font-bold text-neutral-900">「{{ selectedSubject.title }}」知识体系</h3>
              <button
                @click="openNodeAdd('stage', selectedSubject.id)"
                class="px-3 py-1.5 bg-secondary text-white rounded-lg hover:bg-secondary/90 text-sm flex items-center gap-1"
              >
                <i class="fa fa-plus"></i>添加年段
              </button>
            </div>

            <!-- 年段 -->
            <div v-for="stage in childrenOf(selectedSubject.id)" :key="stage.id" class="border border-neutral-200 rounded-xl overflow-hidden">
              <div class="flex items-center gap-3 bg-neutral-50 px-4 py-3">
                <button @click="toggleExpand(stage.id)" class="text-neutral-400 hover:text-primary w-5">
                  <i class="fa" :class="expanded[stage.id] ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                </button>
                <span class="font-bold text-neutral-900 flex-1">{{ stage.title }}</span>
                <span v-if="!stage.isEnable" class="text-xs px-2 py-0.5 rounded-full bg-neutral-200 text-neutral-500">停用</span>
                <span class="text-xs text-neutral-400">排序 {{ stage.orderNum }}</span>
                <button @click="openNodeAdd('grade', stage.id)" class="text-xs text-primary hover:underline">
                  <i class="fa fa-plus"></i> 年级
                </button>
                <button @click="openNodeEdit(stage)" class="text-neutral-400 hover:text-primary p-1"><i class="fa fa-pencil"></i></button>
                <button @click="deleteNode(stage)" class="text-neutral-400 hover:text-red-500 p-1"><i class="fa fa-trash-o"></i></button>
              </div>

              <!-- 年级 -->
              <div v-if="expanded[stage.id]" class="px-4 py-2 space-y-2">
                <div v-for="grade in childrenOf(stage.id)" :key="grade.id" class="border border-neutral-100 rounded-lg">
                  <div class="flex items-center gap-3 px-3 py-2 bg-white">
                    <button @click="toggleExpand(grade.id)" class="text-neutral-400 hover:text-primary w-5">
                      <i class="fa" :class="expanded[grade.id] ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                    </button>
                    <span class="font-medium text-neutral-800 flex-1">{{ grade.title }}</span>
                    <span v-if="!grade.isEnable" class="text-xs px-2 py-0.5 rounded-full bg-neutral-200 text-neutral-500">停用</span>
                    <span class="text-xs text-neutral-400">排序 {{ grade.orderNum }}</span>
                    <button @click="openNodeAdd('term', grade.id)" class="text-xs text-primary hover:underline">
                      <i class="fa fa-plus"></i> 学期
                    </button>
                    <button @click="openNodeEdit(grade)" class="text-neutral-400 hover:text-primary p-1"><i class="fa fa-pencil"></i></button>
                    <button @click="deleteNode(grade)" class="text-neutral-400 hover:text-red-500 p-1"><i class="fa fa-trash-o"></i></button>
                  </div>

                  <!-- 学期 -->
                  <div v-if="expanded[grade.id]" class="px-3 py-2 space-y-2 bg-neutral-50/50">
                    <div v-for="term in childrenOf(grade.id)" :key="term.id" class="border border-neutral-100 rounded-lg bg-white">
                      <div class="flex items-center gap-3 px-3 py-2">
                        <button @click="toggleExpand(term.id)" class="text-neutral-400 hover:text-primary w-5">
                          <i class="fa" :class="expanded[term.id] ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                        </button>
                        <span class="font-medium text-neutral-800 flex-1">{{ term.title }}</span>
                        <span v-if="!term.isEnable" class="text-xs px-2 py-0.5 rounded-full bg-neutral-200 text-neutral-500">停用</span>
                        <span class="text-xs text-neutral-400">排序 {{ term.orderNum }}</span>
                        <button @click="openNodeAdd('unit', term.id)" class="text-xs text-primary hover:underline">
                          <i class="fa fa-plus"></i> 单元
                        </button>
                        <button @click="openNodeEdit(term)" class="text-neutral-400 hover:text-primary p-1"><i class="fa fa-pencil"></i></button>
                        <button @click="deleteNode(term)" class="text-neutral-400 hover:text-red-500 p-1"><i class="fa fa-trash-o"></i></button>
                      </div>

                      <!-- 单元 -->
                      <div v-if="expanded[term.id]" class="px-3 pb-3 pt-1 flex flex-wrap gap-2 bg-neutral-50/50 rounded-b-lg">
                        <div
                          v-for="unit in childrenOf(term.id)"
                          :key="unit.id"
                          class="group flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm"
                          :class="unit.isEnable ? 'border-neutral-200 bg-white text-neutral-700' : 'border-neutral-200 bg-neutral-100 text-neutral-400'"
                        >
                          <span>{{ unit.title }}</span>
                          <button @click="openNodeEdit(unit)" class="opacity-0 group-hover:opacity-100 text-neutral-400 hover:text-primary">
                            <i class="fa fa-pencil"></i>
                          </button>
                          <button @click="deleteNode(unit)" class="opacity-0 group-hover:opacity-100 text-neutral-400 hover:text-red-500">
                            <i class="fa fa-trash-o"></i>
                          </button>
                        </div>
                        <button
                          v-if="childrenOf(term.id).length === 0"
                          @click="openNodeAdd('unit', term.id)"
                          class="px-3 py-1.5 rounded-full border border-dashed border-neutral-300 text-neutral-400 text-sm hover:border-primary hover:text-primary"
                        >
                          <i class="fa fa-plus"></i> 添加单元
                        </button>
                      </div>
                    </div>
                    <button
                      v-if="childrenOf(grade.id).length === 0"
                      @click="openNodeAdd('term', grade.id)"
                      class="w-full py-2 rounded-lg border border-dashed border-neutral-300 text-neutral-400 text-sm hover:border-primary hover:text-primary"
                    >
                      <i class="fa fa-plus"></i> 添加学期
                    </button>
                  </div>
                </div>
                <button
                  v-if="childrenOf(stage.id).length === 0"
                  @click="openNodeAdd('grade', stage.id)"
                  class="w-full py-2 rounded-lg border border-dashed border-neutral-300 text-neutral-400 text-sm hover:border-primary hover:text-primary"
                >
                  <i class="fa fa-plus"></i> 添加年级
                </button>
              </div>
            </div>
            <button
              v-if="childrenOf(selectedSubject.id).length === 0"
              @click="openNodeAdd('stage', selectedSubject.id)"
              class="w-full py-3 rounded-xl border border-dashed border-neutral-300 text-neutral-400 text-sm hover:border-primary hover:text-primary"
            >
              <i class="fa fa-plus"></i> 添加年段（如：小学、初中、高中）
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 学科编辑弹窗 -->
    <el-dialog v-model="subjectDialogVisible" :title="isSubjectEdit ? '编辑学科' : '新增学科'" width="420px" :close-on-click-modal="false" destroy-on-close>
      <div class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-neutral-700 mb-1">学科名称 <span class="text-red-500">*</span></label>
          <input v-model="subjectForm.title" type="text" placeholder="如：语文" class="w-full px-4 py-2 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm" />
        </div>
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-neutral-700 mb-1">排序号</label>
            <input v-model.number="subjectForm.orderNum" type="number" class="w-full px-4 py-2 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm" />
          </div>
          <div class="flex items-end gap-2 pb-1">
            <label class="text-sm font-medium text-neutral-700">启用：</label>
            <el-switch v-model="subjectForm.isEnable" />
          </div>
        </div>
      </div>
      <template #footer>
        <div class="flex justify-end gap-3">
          <button @click="subjectDialogVisible = false" class="px-4 py-2 rounded-lg border border-neutral-300 text-neutral-700 text-sm">取消</button>
          <button @click="saveSubject" class="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium">确定</button>
        </div>
      </template>
    </el-dialog>

    <!-- 知识节点编辑弹窗 -->
    <el-dialog v-model="nodeDialogVisible" :title="nodeTitle" width="420px" :close-on-click-modal="false" destroy-on-close>
      <div class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-neutral-700 mb-1">{{ typeName }}名称 <span class="text-red-500">*</span></label>
          <input
            v-model="nodeForm.title"
            type="text"
            :placeholder="nodePlaceholder"
            class="w-full px-4 py-2 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm"
          />
        </div>
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-neutral-700 mb-1">排序号</label>
            <input v-model.number="nodeForm.orderNum" type="number" class="w-full px-4 py-2 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm" />
          </div>
          <div class="flex items-end gap-2 pb-1">
            <label class="text-sm font-medium text-neutral-700">启用：</label>
            <el-switch v-model="nodeForm.isEnable" />
          </div>
        </div>
      </div>
      <template #footer>
        <div class="flex justify-end gap-3">
          <button @click="nodeDialogVisible = false" class="px-4 py-2 rounded-lg border border-neutral-300 text-neutral-700 text-sm">取消</button>
          <button @click="saveNode" class="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium">确定</button>
        </div>
      </template>
    </el-dialog>

    <!-- 批量导入弹窗 -->
    <el-dialog v-model="importVisible" title="批量导入学科知识体系" width="600px" :close-on-click-modal="false">
      <div class="space-y-4">
        <div class="text-sm bg-neutral-50 rounded-lg p-3 text-neutral-600 leading-relaxed">
          <p class="font-bold mb-1">格式说明（每行一条，用 / 分隔层级，最多 5 级）：</p>
          <p class="font-mono text-xs bg-white border border-neutral-200 rounded p-2 mt-1">
            学科/年段/年级/学期/单元<br />
            语文/小学/二年级/下学期/第一单元<br />
            数学/初中/七年级/上学期
          </p>
          <ul class="text-xs text-neutral-400 mt-2 space-y-0.5 list-disc list-inside">
            <li>层级顺序固定：学科 → 年段 → 年级 → 学期 → 单元，可只写到任意一级</li>
            <li>已存在的学科/节点自动跳过，只创建缺失的部分</li>
          </ul>
        </div>
        <el-input
          v-model="importText"
          type="textarea"
          :rows="10"
          placeholder="在此粘贴学科体系数据，每行一条..."
        />
        <div v-if="importResult" class="text-sm bg-green-50 text-green-700 rounded-lg p-3">
          <p>
            导入完成：新建学科 <b>{{ importResult.newSubjects }}</b> 个，
            新建节点 <b>{{ importResult.newNodes }}</b> 个，
            复用已有 <b>{{ importResult.reused }}</b> 个
          </p>
          <p v-if="importResult.errors" class="text-xs mt-1 text-amber-600 break-all">
            {{ importResult.errors }}
          </p>
        </div>
      </div>
      <template #footer>
        <div class="flex justify-end gap-3">
          <button @click="importVisible = false" class="px-4 py-2 rounded-lg border border-neutral-300 text-neutral-700 text-sm">关闭</button>
          <button
            @click="submitBatchImport"
            :disabled="importing"
            class="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium disabled:opacity-50"
          >
            {{ importing ? '导入中...' : '开始导入' }}
          </button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ElMessageBox, ElMessage } from "element-plus";

const TYPE_NAMES = { stage: "年段", grade: "年级", term: "学期", unit: "单元" };
const TYPE_PLACEHOLDERS = {
  stage: "如：小学、初中、高中",
  grade: "如：一年级、三年级",
  term: "如：上学期、下学期",
  unit: "如：第一单元、第二单元",
};

export default {
  name: "EduTreeManager",
  data() {
    return {
      loading: false,
      subjectList: [],
      nodeList: [],
      selectedSubject: null,
      expanded: {},
      // 学科表单
      subjectDialogVisible: false,
      isSubjectEdit: false,
      subjectForm: { id: "", title: "", orderNum: 0, isEnable: true },
      // 节点表单
      nodeDialogVisible: false,
      isNodeEdit: false,
      nodeForm: { id: "", parentId: "", type: "stage", title: "", orderNum: 0, isEnable: true },
      // 批量导入
      importVisible: false,
      importing: false,
      importText: "",
      importResult: null,
    };
  },
  computed: {
    typeName() {
      return TYPE_NAMES[this.nodeForm.type] || "节点";
    },
    nodePlaceholder() {
      return TYPE_PLACEHOLDERS[this.nodeForm.type] || "";
    },
    nodeTitle() {
      return (this.isNodeEdit ? "编辑" : "新增") + this.typeName;
    },
  },
  created() {
    this.getData();
  },
  methods: {
    /* ---------- 批量导入 ---------- */
    openBatchImport() {
      this.importResult = null;
      this.importVisible = true;
    },
    async submitBatchImport() {
      if (!this.importText.trim()) {
        ElMessage.warning("请先粘贴要导入的学科体系数据");
        return;
      }
      this.importing = true;
      try {
        const data = await this.$aesEncrypt({ text: this.importText });
        const res = await this.$axios({
          url: "edu/admin/batchimport",
          method: "POST",
          data: { data },
        });
        if (res.code === 200) {
          this.importResult = res.data;
          ElMessage.success("导入完成");
          this.getData();
        } else {
          ElMessage.error(res.msg || "导入失败");
        }
      } catch (e) {
        ElMessage.error("导入失败，请稍后重试");
      } finally {
        this.importing = false;
      }
    },

    async getData() {
      this.loading = true;
      try {
        const subData = await this.$aesEncrypt({});
        const subRes = await this.$axios({ url: "subinfo/admin/list", method: "POST", data: { data: subData } });
        if (subRes.code === 200) this.subjectList = subRes.data || [];

        const nodeData = await this.$aesEncrypt({});
        const nodeRes = await this.$axios({ url: "edu/admin/list", method: "POST", data: { data: nodeData } });
        if (nodeRes.code === 200) {
          this.nodeList = nodeRes.data || [];
          // 默认展开已加载的年段
          this.nodeList.filter((n) => n.type === "stage").forEach((n) => (this.expanded[n.id] = true));
        }

        // 保持选中学科刷新后仍在列表中
        if (this.selectedSubject) {
          const cur = this.subjectList.find((s) => s.id === this.selectedSubject.id);
          this.selectedSubject = cur || null;
        }
      } finally {
        this.loading = false;
      }
    },
    childrenOf(parentId) {
      return this.nodeList
        .filter((n) => n.parentId === parentId)
        .sort((a, b) => (a.orderNum || 0) - (b.orderNum || 0));
    },
    toggleExpand(id) {
      this.expanded[id] = !this.expanded[id];
    },
    selectSubject(sub) {
      this.selectedSubject = sub;
    },

    /* ---------- 学科 CRUD ---------- */
    openSubjectAdd() {
      this.isSubjectEdit = false;
      this.subjectForm = { id: "", title: "", orderNum: (this.subjectList.length + 1) * 10, isEnable: true };
      this.subjectDialogVisible = true;
    },
    openSubjectEdit(sub) {
      this.isSubjectEdit = true;
      this.subjectForm = { ...sub };
      this.subjectDialogVisible = true;
    },
    async saveSubject() {
      if (!this.subjectForm.title.trim()) {
        ElMessage.warning("请填写学科名称");
        return;
      }
      const payload = { ...this.subjectForm, title: this.subjectForm.title.trim() };
      if (!this.isSubjectEdit) delete payload.id;
      const data = await this.$aesEncrypt(payload);
      const res = await this.$axios({ url: "subinfo", method: "PUT", data: { data } });
      if (res.code === 200) {
        ElMessage.success(this.isSubjectEdit ? "编辑成功" : "新增成功");
        this.subjectDialogVisible = false;
        this.getData();
      } else {
        ElMessage.error(res.msg || "保存失败");
      }
    },
    deleteSubject(sub) {
      ElMessageBox.confirm(`确定删除学科「${sub.title}」吗？`, "删除学科", {
        confirmButtonText: "删除",
        cancelButtonText: "取消",
        type: "error",
      }).then(() => {
        this.$axios({ url: `subinfo/${sub.id}`, method: "DELETE" }).then((res) => {
          if (res.code === 200) {
            ElMessage.success("删除成功");
            if (this.selectedSubject && this.selectedSubject.id === sub.id) this.selectedSubject = null;
            this.getData();
          } else {
            ElMessage.error(res.msg || "删除失败");
          }
        });
      });
    },

    /* ---------- 知识节点 CRUD ---------- */
    openNodeAdd(type, parentId) {
      this.isNodeEdit = false;
      this.nodeForm = {
        id: "",
        parentId,
        type,
        title: "",
        orderNum: (this.childrenOf(parentId).length + 1) * 10,
        isEnable: true,
      };
      this.nodeDialogVisible = true;
    },
    openNodeEdit(node) {
      this.isNodeEdit = true;
      this.nodeForm = { ...node };
      this.nodeDialogVisible = true;
    },
    async saveNode() {
      if (!this.nodeForm.title.trim()) {
        ElMessage.warning("请填写名称");
        return;
      }
      const payload = { ...this.nodeForm, title: this.nodeForm.title.trim() };
      if (!this.isNodeEdit) delete payload.id;
      const data = await this.$aesEncrypt(payload);
      const res = await this.$axios({ url: "edu", method: "PUT", data: { data } });
      if (res.code === 200) {
        ElMessage.success(this.isNodeEdit ? "编辑成功" : "新增成功");
        this.nodeDialogVisible = false;
        this.getData();
      } else {
        ElMessage.error(res.msg || "保存失败");
      }
    },
    deleteNode(node) {
      ElMessageBox.confirm(
        `确定删除${TYPE_NAMES[node.type]}「${node.title}」吗？`,
        "删除" + TYPE_NAMES[node.type],
        { confirmButtonText: "删除", cancelButtonText: "取消", type: "error" }
      ).then(() => {
        this.$axios({ url: `edu/${node.id}`, method: "DELETE" }).then((res) => {
          if (res.code === 200) {
            ElMessage.success("删除成功");
            this.getData();
          } else {
            ElMessage.error(res.msg || "删除失败");
          }
        });
      });
    },
  },
};
</script>
